import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  GetWoundByIdResponse,
  WoundAssessmentsDto,
  GetWoundHistoryResponseItem,
  WoundStatus,
} from 'app/core/models/wound/wound.model';
import { UlcerWoundType } from 'app/core/models/ulcer-wound-type/ulcer-wound-type.model';
import { UlcerWoundTypeService } from 'app/core/services/ulcer-wound-type/ulcer-wound-type.service';
import { SymptomService } from 'app/core/services/symptom/symptom.service';
import { Symptom } from 'app/core/models/symptom/symptom.model';
import { WoundService } from 'app/core/services/wound/wound.service';
import { ConclusionService } from 'app/core/services/conclusion/conclusion.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type PriorManagementValue =
  | number
  | string
  | { id?: number | string; name?: string };

@Component({
  selector: 'app-wound-assessment-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './wound-assessment-detail.component.html',
  styleUrls: ['./wound-assessment-detail.component.scss'],
})
export class WoundAssessmentDetailComponent implements OnChanges, OnInit {
  @Input() wound: GetWoundByIdResponse | null = null;
  @Input() initialAssessmentId: string | null = null;

  selectedAssessment: WoundAssessmentsDto | null = null;
  priorManagementOptions: Array<{ id: number; name: string }> = [];
  ulcerWoundTypeOptions: UlcerWoundType[] = [];
  symptomOptions: Symptom[] = [];
  healingStatusOptions: Array<{ id: number; name: string }> = [];

  activeTab: 'detail' | 'history' = 'detail';

  woundHistory: GetWoundHistoryResponseItem[] = [];
  isLoadingHistory: boolean = false;
  eWoundStatus = WoundStatus;

  constructor(
    private translate: TranslateService,
    private ulcerWoundTypeService: UlcerWoundTypeService,
    private symptomService: SymptomService,
    private woundService: WoundService,
    private conclusionService: ConclusionService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.getListUlcerWoundType();
    this.initPriorManagementOptions();
    this.initHealingStatusOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wound'] && this.wound) {
      this.initData();
      this.loadWoundHistory();
    }
  }


  private initData(): void {
    if (!this.wound?.woundAssessments?.length) {
      this.selectedAssessment = null;
      return;
    }


    if (this.initialAssessmentId) {
      this.selectedAssessment =
        this.wound.woundAssessments.find(
          (a) => a.id === this.initialAssessmentId,
        ) || this.wound.woundAssessments[0];
    } else {
      this.selectedAssessment = this.wound.woundAssessments[0];
    }

  }


  getVacTypeLabel(vacType: number): string {
    const types: { [key: number]: string } = {
      1: 'VAC Type 1',
      2: 'VAC Type 2',
      3: 'VAC Type 3',
    };
    return types[vacType] || `VAC Type ${vacType}`;
  }

  async getListUlcerWoundType() {
    const res = await this.ulcerWoundTypeService.getListUlcerWoundType();
    this.ulcerWoundTypeOptions = res.data;
  }

  async getListSymptom() {
    const res = await this.symptomService.getListSymptom();
    this.symptomOptions = res.data;
  }

  getPriorManagementName(id: number | string | null | undefined): string {
    if (id === null || id === undefined || id === '') {
      return '-';
    }

    return (
      this.priorManagementOptions.find((opt) => opt.id === Number(id))?.name ||
      '-'
    );
  }

  getPriorManagementDisplay(
    value: PriorManagementValue | PriorManagementValue[] | null | undefined,
  ): string {
    const values: PriorManagementValue[] = Array.isArray(value)
      ? value
      : value !== null && value !== undefined && value !== ''
        ? [value]
        : [];

    const names = values
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return item.name || this.getPriorManagementName(item.id);
        }

        return this.getPriorManagementName(item as number | string);
      })
      .filter((name) => name && name !== '-');

    return names.length ? names.join(', ') : '-';
  }

  getHealingStatusName(id: number | string | null | undefined): string {
    if (id === null || id === undefined || id === '') {
      return '-';
    }

    return (
      this.healingStatusOptions.find((opt) => opt.id === Number(id))?.name ||
      '-'
    );
  }

  getPainLevelDisplay(painLevel: number | string | null | undefined): string {
    if (painLevel === null || painLevel === undefined || painLevel === '') {
      return '-';
    }

    return `${painLevel} / 10`;
  }

  hasTreatmentInfo(wound: any): boolean {
    return !!(
      wound?.woundAntibiotics?.length ||
      wound?.woundDebridementDtos?.length ||
      wound?.woundWoundCares?.length
    );
  }

  switchTab(tab: 'detail' | 'history'): void {
    this.activeTab = tab;
  }

  initHealingStatusOptions() {
    this.healingStatusOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.worsening',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.stable',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.woundAssessment.healingStatusOptions.improving',
        ),
      },
    ];
  }

  initPriorManagementOptions() {
    this.priorManagementOptions = [
      {
        id: 1,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.systemicAntibiotics',
        ),
      },
      {
        id: 2,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.topicalAntibiotics',
        ),
      },
      {
        id: 3,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.dressings',
        ),
      },
      {
        id: 4,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.woundCareProducts',
        ),
      },
      {
        id: 5,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.solutions',
        ),
      },
      {
        id: 6,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.topicalGrowthFactors',
        ),
      },
      {
        id: 7,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.intralesionalGrowthFactors',
        ),
      },
      {
        id: 8,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.biophysicalAgents',
        ),
      },
      {
        id: 9,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.exosomeTherapy',
        ),
      },
      {
        id: 10,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.plateletRichPlasma',
        ),
      },
      {
        id: 11,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.stemCellTherapy',
        ),
      },
      {
        id: 12,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.debridement',
        ),
      },
      {
        id: 13,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.vacuumAssistedClosure',
        ),
      },
      {
        id: 14,
        name: this.translate.instant(
          'patientCreate.woundAssessment.priorManagementOptions.surgery',
        ),
      },
    ];
  }

  getStatusTranslationKey(status: WoundStatus): string {
    const statusMap: { [key in WoundStatus]: string } = {
      [WoundStatus.Created]: 'woundStatuses.created',
      [WoundStatus.WaitingDoctorAssignment]: 'woundStatuses.waitingDoctorAssignment',
      [WoundStatus.DoctorAssigned]: 'woundStatuses.doctorAssigned',
      [WoundStatus.TreatmentDecisionCreated]: 'woundStatuses.treatmentDecisionCreated',
      [WoundStatus.TreatmentCompleted]: 'woundStatuses.treatmentCompleted',
      [WoundStatus.Processing]: 'woundStatuses.processing',
    };
    return statusMap[status] || '';
  }

  async loadWoundHistory(): Promise<void> {
    if (!this.wound) return;

    this.isLoadingHistory = true;
    this.woundHistory = [];

    try {
      const response = await this.woundService
        .getWoundHistory(this.wound.id)
        .toPromise();

      if (response?.data) {
        this.woundHistory = response.data;
        console.log('🔍 DEBUG: Wound history loaded:', this.woundHistory);
        this.woundHistory.forEach((h, i) => {
            console.log(`Item ${i} dates:`, {
                status: h.woundStatus,
                created: h.createdDate,
                completed: h.completedDate,
                typeOfCreated: typeof h.createdDate,
                typeOfCompleted: typeof h.completedDate
            });
        });
      }
    } catch (error) {
      console.error('❌ Error loading wound history:', error);
    } finally {
      this.isLoadingHistory = false;
    }
  }
  getTreatmentType(treatment: string): string {
    if (!treatment) return '';
    const parts = treatment.split(':');
    return parts[0] ? parts[0].trim() : '';
  }

  getTreatmentDescription(treatment: string): string {
    if (!treatment) return '';
    const parts = treatment.split(':');
    return parts.length > 1 ? parts.slice(1).join(':').trim() : '';
  }
}
